import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getAuth,
  multiFactor,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import { firebaseAuth } from '../../providers/AuthProvider';

const STAGE = {
  IDLE: 'idle',
  GENERATED: 'generated',
  ENROLLED: 'enrolled',
};

const MFA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleSignout } = useContext(firebaseAuth);
  const auth = getAuth();

  const [stage, setStage] = useState(STAGE.IDLE);
  const [secret, setSecret] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !user.isAuthenticated) {
      navigate('/manage/sign-in', { state: { from: location }, replace: true });
    }
  }, [user, navigate, location]);

  const isStaleSession = (err) =>
    err?.code === 'auth/requires-recent-login' ||
    err?.code === 'auth/user-token-expired';

  const handleStaleSession = async () => {
    navigate('/manage/sign-in?reauth=mfa', {
      state: { from: location },
      replace: true,
    });
    await handleSignout();
  };

  const startEnrollment = async () => {
    setError(null);
    setBusy(true);
    try {
      const session = await multiFactor(auth.currentUser).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
      const url = totpSecret.generateQrCodeUrl(
        auth.currentUser.email || 'user',
        'Porsche eZentrum',
      );
      setSecret(totpSecret);
      setQrUrl(url);
      setStage(STAGE.GENERATED);
    } catch (err) {
      console.error('Failed to generate TOTP secret', err);
      if (isStaleSession(err)) {
        await handleStaleSession();
        return;
      }
      setError(err.message || 'Could not start MFA enrollment.');
    } finally {
      setBusy(false);
    }
  };

  const completeEnrollment = async (e) => {
    e.preventDefault();
    setError(null);
    if (!secret) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        code.trim(),
      );
      await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator App');
      setStage(STAGE.ENROLLED);
      toast.success('MFA enrolled. Please sign in again to continue.');
      setTimeout(() => handleSignout(), 2000);
    } catch (err) {
      console.error('Failed to enroll TOTP factor', err);
      if (isStaleSession(err)) {
        await handleStaleSession();
        return;
      }
      setError(
        err.code === 'auth/invalid-verification-code'
          ? 'That code did not match. Try again with the current code from your app.'
          : err.message || 'Could not enroll MFA.',
      );
    } finally {
      setBusy(false);
    }
  };

  const alreadyEnrolled = !!user?.multiFactor;

  if (alreadyEnrolled && stage !== STAGE.ENROLLED) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
          MFA is enabled
        </h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          Contact{' '}
          <a className="text-zinc-900 hover:underline dark:text-white" href="mailto:support@core12.com">
            support@core12.com
          </a>{' '}
          to reset your MFA configuration.
        </p>
      </div>
    );
  }

  if (stage === STAGE.ENROLLED) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">MFA enrolled</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          Signing you out so you can sign back in with your authenticator code…
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
        Set up multi-factor authentication
      </h1>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
        Porsche eZentrum requires MFA via an authenticator app (Google Authenticator,
        1Password, Authy, etc.).
      </p>

      {stage === STAGE.IDLE && (
        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={startEnrollment}
            disabled={busy}
            className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {busy ? 'Generating…' : 'Begin enrollment'}
          </button>
          <button
            type="button"
            onClick={handleSignout}
            className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
          >
            Sign out
          </button>
        </div>
      )}

      {stage === STAGE.GENERATED && (
        <div className="mt-6">
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            <span className="font-semibold text-zinc-900 dark:text-white">1.</span> Open your authenticator
            app and scan this code, or enter the secret manually.
          </p>
          <div className="mt-4 flex flex-col items-start gap-3">
            <div className="rounded-lg bg-zinc-50 p-3">
              {qrUrl && <QRCodeSVG value={qrUrl} size={176} />}
            </div>
            <code className="rounded bg-zinc-100 px-2 py-1 text-xs text-zinc-700 ring-1 ring-inset ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-300 dark:ring-white/10">
              {secret?.secretKey}
            </code>
          </div>
          <p className="mt-6 text-sm text-zinc-700 dark:text-zinc-300">
            <span className="font-semibold text-zinc-900 dark:text-white">2.</span> Enter the current
            6-digit code to verify.
          </p>
          <form onSubmit={completeEnrollment} className="mt-3 space-y-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="block w-full rounded-md bg-zinc-50 px-3 py-2 text-center text-lg tracking-widest text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:focus:ring-white"
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              {busy ? 'Verifying…' : 'Verify and enroll'}
            </button>
          </form>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20">
          {error}
        </div>
      )}
    </div>
  );
};

export default MFA;
